/**
 * Reducir una foto antes de guardarla (design.md 7.10).
 *
 * La foto se dibuja en un canvas al lado mayor de 1,280 px y se vuelve a
 * codificar como JPEG. Tres razones, todas importantes:
 *
 * 1. Una foto del iPhone pesa 3 MB o más; reducida queda en unos 200 KB, y el
 *    respaldo con fotos tiene que seguir cabiendo en Archivos y en iCloud.
 * 2. Al volver a codificarla se quedan fuera los metadatos EXIF, que en el
 *    iPhone llevan la UBICACIÓN donde se tomó. DuoFit no guarda eso.
 * 3. La orientación de la cámara se aplica al dibujarla, así que la foto se
 *    guarda derecha y no depende de que quien la mire respete el EXIF.
 *
 * Lo que se decide (a qué tamaño, con qué calidad, qué archivos se aceptan)
 * vive en @/domain/fotos, que es código puro y tiene pruebas. Aquí solo está
 * lo que necesita el navegador.
 */

import {
  CALIDAD_JPEG,
  LADO_MAXIMO,
  TEXTO_ARCHIVO_INVALIDO,
  medidaReducida,
  revisarArchivo,
} from '@/domain/fotos';
import { ErrorValidacion } from '@/domain/errors';

const NO_SE_PUDO =
  'Esa imagen no se pudo abrir. Puede estar dañada o ser de un formato que el iPhone no ' +
  'convierte. Toma la foto otra vez o elige otra.';

async function abrirImagen(archivo: Blob): Promise<ImageBitmap> {
  if (typeof createImageBitmap !== 'function') {
    throw new ErrorValidacion(
      'Este navegador no puede preparar fotos. Abre DuoFit en Safari o en Chrome actualizados.',
    );
  }
  try {
    // 'from-image' aplica la orientación de la cámara al dibujarla.
    return await createImageBitmap(archivo, { imageOrientation: 'from-image' });
  } catch {
    throw new ErrorValidacion(NO_SE_PUDO);
  }
}

function aJpeg(lienzo: HTMLCanvasElement, calidad: number): Promise<Blob> {
  return new Promise((resolver, rechazar) => {
    lienzo.toBlob(
      (blob) => {
        if (blob === null || blob.size === 0) rechazar(new ErrorValidacion(NO_SE_PUDO));
        else resolver(blob);
      },
      'image/jpeg',
      calidad,
    );
  });
}

/**
 * Revisa el archivo elegido y devuelve el JPEG ya reducido, listo para
 * guardarse. Lanza ErrorValidacion con un mensaje que se puede mostrar.
 */
export async function reducirImagen(
  archivo: File | Blob,
  ladoMaximo = LADO_MAXIMO,
  calidad = CALIDAD_JPEG,
): Promise<Blob> {
  const problema = revisarArchivo({ type: archivo.type, size: archivo.size });
  if (problema !== null) throw new ErrorValidacion(TEXTO_ARCHIVO_INVALIDO[problema]);

  const imagen = await abrirImagen(archivo);
  try {
    const { ancho, alto } = medidaReducida(imagen.width, imagen.height, ladoMaximo);
    if (ancho === 0 || alto === 0) throw new ErrorValidacion(NO_SE_PUDO);

    const lienzo = document.createElement('canvas');
    lienzo.width = ancho;
    lienzo.height = alto;
    const pincel = lienzo.getContext('2d');
    if (pincel === null) throw new ErrorValidacion(NO_SE_PUDO);
    // Fondo blanco: un PNG con transparencia quedaría negro en JPEG.
    pincel.fillStyle = '#ffffff';
    pincel.fillRect(0, 0, ancho, alto);
    pincel.drawImage(imagen, 0, 0, ancho, alto);

    return await aJpeg(lienzo, calidad);
  } finally {
    imagen.close();
  }
}
